import $ from 'jquery';
import { __ } from '@wordpress/i18n';
import getFormData from './helpers/get-form-data';
import GCOAIField from './field';

interface GCOAIFieldImageArgs {
	formId: number;
	fieldId: number;
	triggerMethod: string;
	size: string;
	nonce: string;
}

class GCOAIFieldImage extends GCOAIField {
	public size: string;

	public images: ({ url: string } | { b64_json: string })[] = [];

	public $inputResultId: JQuery<HTMLInputElement>;

	public request?: JQuery.jqXHR;

	public constructor(args: GCOAIFieldImageArgs) {
		super(args);

		this.size = args.size;

		this.$inputResultId =
			this.$field.find<HTMLInputElement>('.gcoai-result-id');

		this.init();
	}

	rehydrate() {
		this.images = JSON.parse(this.$inputResponse.val()!);
		this.displayImages();
	}

	public addPlaceholder = () => {
		this.$output
			.html(this.loadingText)
			.show()
			.css({
				width: this.size.split('x')[0] + 'px',
				height: this.size.split('x')[1] + 'px',
			})
			.addClass('gcoai-loading');
	};

	public generate = () => {
		// Cancel any previous requests
		if (this.request) {
			this.request.abort();
		}

		// Hide buttons
		this.$buttonTrigger.hide();
		this.$buttonRegenerate.hide();

		// Disable the submit button while we're loading.
		this.disableSubmitButton();

		this.addPlaceholder();

		this.request = $.ajax({
			url:
				window.gc_openai_field_image_strings!.ajaxUrl +
				'?action=gc_openai_generate_image',
			contentType: 'application/json',
			dataType: 'json',
			data: JSON.stringify({
				formData: getFormData(this.$form[0]),
				formId: this.formId,
				fieldId: this.fieldId,
				nonce: this.nonce,
				entryNonce: this.entryNonce,
			}),
			method: 'POST',
		}).done((response) => {
			// Clear the output
			this.$output.html('').removeClass('gcoai-loading');

			// Enable the submit button again.
			this.enableSubmitButton();

			if (response?.errors || response?.success === false) {
				let errorMessage = __(
					'There was an error generating the image.',
					'gc-openai'
				);

				// A message the server sent for the field to show, e.g. a source image OpenAI won't take.
				if (typeof response?.data?.message === 'string') {
					errorMessage = response.data.message;
				} else if (response?.errors || response?.data) {
					// Check if it's a timeout error and provide more helpful message
					const errorText =
						response?.data || JSON.stringify(response?.errors);
					if (
						errorText.includes('timed out') ||
						errorText.includes('timeout')
					) {
						errorMessage = __(
							'Image generation timed out. High quality images may take longer to generate. Please try again.',
							'gc-openai'
						);
					}
				}

				this.$output
					.html(`<div class="gcoai-error">${errorMessage}</div>`)
					.css({
						width: '',
						height: '',
					})
					.removeClass('gcoai-loading');

				/*
				 * A failed first request has no result to regenerate, so restore its Generate button. If an existing image's
				 * regeneration failed, keep the established Regenerate action instead.
				 */
				if (this.$inputResponse.val()) {
					this.$buttonRegenerate.show();
				} else {
					this.$buttonTrigger.show();
				}

				return;
			}

			// Set images and display them.
			this.images = response.images;
			this.displayImages();

			// Store the image data in a hidden input
			this.$inputResponse.val(JSON.stringify(response.images));
			this.$inputResultId.val(response.resultId);

			// Trigger an event on $inputResponse so that GF conditional logic can pick up the change.
			this.$inputResponse.trigger('change');

			// Show the regenerate button
			this.$buttonRegenerate.show();
		});
	};

	public displayImages = () => {
		this.$output.html('');

		for (const image of this.images) {
			let src = '';

			if (image.url) {
				// DALL-E models return URLs
				src = image.url;
			} else if (image.b64_json) {
				// GPT Image models return base64
				src = `data:image/png;base64,${image.b64_json}`;
			}

			if (src) {
				this.$output.append(
					`<img src="${src}" alt="${__(
						'Generated image',
						'gc-openai'
					)}" />`
				);
			}
		}

		this.$output.show();
	};
}

(window as any).GCOAIFieldImage = GCOAIFieldImage;
